import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { useSbSelector } from '../../reducers/useSbSelector';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useShowItemLocationBadge } from '../../shared/hooks/useShowItemLocationBadge';

import { SbSharedRecentlySoldItems_itemSearch$key } from './__generated__/SbSharedRecentlySoldItems_itemSearch.graphql';
import { SbSharedRecentlySoldItems_viewer$key } from './__generated__/SbSharedRecentlySoldItems_viewer.graphql';

import styles from './SbSharedRecentlySoldItems.scss';
import dibsCss from 'dibs-css';
type Props = {
    viewer: SbSharedRecentlySoldItems_viewer$key;
    itemSearch: SbSharedRecentlySoldItems_itemSearch$key;
};

export const SbSharedRecentlySoldItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
            }
        `,
        viewerRef
    );
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                soldResults {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SbSharedItemTrackingProvider_item
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const isClient = useSbSelector(store => store.relayVariables.variables.isClient);
    const { isMobile } = useServerVarsContext();
    const showItemLocationBadge = useShowItemLocationBadge();

    const thinBrowseSoldItems = (itemSearch?.soldResults?.edges || [])
        .map(item => item?.node?.item || null)
        .filter(filterFalsy);

    if (!thinBrowseSoldItems.length) {
        return null;
    }

    return (
        <SbSharedItemTrackingProvider
            viewer={viewer}
            itemSearch={itemSearch}
            items={thinBrowseSoldItems}
            pageType="previously available"
        >
            {({ fireItemClickTracking, fireItemImpressionTracking }) => (
                <>
                    <BarHeader
                        title={
                            <FormattedMessage
                                id="sb.SbSharedRecentlySoldItems.title"
                                defaultMessage="Previously Available Items"
                            />
                        }
                    />
                    <QuickViewProvider>
                        {({ quickView }) => {
                            return (
                                <div
                                    className={classnames(dibsCss.flex, dibsCss.flexWrap, {
                                        [styles.container]: isMobile,
                                    })}
                                    data-tn="previously-available-items"
                                >
                                    {thinBrowseSoldItems.map((soldItem, index) => {
                                        return (
                                            <SharedItemTile
                                                isClient={isClient}
                                                key={soldItem?.serviceId}
                                                index={index}
                                                item={soldItem}
                                                showSellerName={false}
                                                showItemLocationBadge={showItemLocationBadge}
                                                viewer={viewer}
                                                quickView={quickView}
                                                itemSearch={itemSearch}
                                                isMobile={isMobile}
                                                onContentVisible={() =>
                                                    fireItemImpressionTracking({
                                                        itemId: soldItem.serviceId,
                                                        index,
                                                    })
                                                }
                                                onClick={() =>
                                                    fireItemClickTracking({
                                                        itemId: soldItem.serviceId,
                                                        index,
                                                    })
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }}
                    </QuickViewProvider>
                </>
            )}
        </SbSharedItemTrackingProvider>
    );
};
